import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import IncompleteQuickEvaluationDialog from '../../shared/homework/IncompleteQuickEvaluationDialog';
import { apiUrl, fetchJson } from '../../apiBase';
import useQuickHomeworkEvaluation, { dateInIstanbul, firstQuickLesson } from '../../shared/homework/useQuickHomeworkEvaluation';

const STATUS_OPTIONS = [
  { value: 'not_done', label: 'Yapmadı', color: 'error' },
  { value: 'missing', label: 'Eksik', color: 'warning' },
  { value: 'done', label: 'Tamamladı', color: 'success' },
];

function studentName(student) {
  return String(
    student?.student_name
      || student?.full_name
      || [student?.first_name || student?.name, student?.last_name || student?.surname]
        .filter(Boolean)
        .join(' ')
      || `Öğrenci ${student?.id || ''}`
  ).trim();
}

export default function QuickHomeworkEvaluationDialog({
  open,
  onClose,
  onCompleted,
  onError,
  schoolId,
  classes,
  teachers,
  initialClassId,
  initialTeacherId,
  isTeacher,
}) {
  const [classId, setClassId] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [lessons, setLessons] = useState([]);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loadingLessons, setLoadingLessons] = useState(false);

  const draft = useQuickHomeworkEvaluation({ students, onError,
    send: async payload => {
      const response = await fetchJson(apiUrl(`/admin/homework-evaluations/${encodeURIComponent(schoolId)}/quick`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const result = await response.json();
      if (!response.ok) { const error = new Error(String(result.detail || 'Değerlendirme kaydedilemedi.')); error.status = response.status; throw error; }
      return result;
    },
    onCompleted: async result => { await onCompleted?.({ ...result, class_id: classId, teacher_id: teacherId }); onClose(); },
  });
  const { lesson, setLesson, assignedDate, setAssignedDate, evaluations, setEvaluations, saving, locked,
    formError, setFormError, confirmIncomplete, setConfirmIncomplete, activeStudents, absentCount,
    evaluatedCount, evaluatedAbsentCount, remainingCount, selectedStudents, setStatus, markAllDone, reset } = draft;

  useEffect(() => {
    if (!open) return;
    const nextTeacherId = String(initialTeacherId || '');
    setClassId(String(initialClassId || classes?.[0]?.id || ''));
    setTeacherId(nextTeacherId);
    reset();
    setStudents([]);
  }, [open, initialClassId, initialTeacherId, classes, teachers, reset]);

  useEffect(() => {
    if (!open || !schoolId || !classId || (!isTeacher && !teacherId)) {
      setLessons([]);
      setLesson('');
      setLoadingLessons(false);
      return;
    }
    let cancelled = false;
    const loadLessons = async () => {
      try {
        const query = new URLSearchParams();
        if (!isTeacher) query.set('teacher_id', teacherId);
        const response = await fetchJson(apiUrl(`/admin/homework-create-options/${encodeURIComponent(schoolId)}?${query}`));
        if (!response.ok) throw new Error('lessons');
        const data = await response.json();
        if (cancelled) return;
        const selectedClass = (data.assigned_sections || []).find((item) => String(item.id) === classId);
        const values = (data.lessons_by_grade?.[selectedClass?.grade_level] || [])
          .map((item) => String(item?.name || '').trim())
          .filter(Boolean);
        setLessons(values);
        // The server orders general homework first for class teachers and the
        // institution's primary branch first for subject teachers.
        setLesson(firstQuickLesson(values));
        if (!values.length) setFormError('Bu öğretmen ve şube için uygun ödev dersi bulunamadı.');
      } catch (_) {
        if (!cancelled) setFormError('Dersler yüklenemedi.');
      } finally {
        if (!cancelled) setLoadingLessons(false);
      }
    };
    setLessons([]);
    setLesson('');
    setLoadingLessons(true);
    setFormError('');
    loadLessons();
    return () => { cancelled = true; };
  }, [open, schoolId, classId, teacherId, isTeacher, setLesson, setFormError]);

  useEffect(() => {
    if (!open || !schoolId || !classId) {
      setStudents([]);
      return;
    }
    let cancelled = false;
    const loadStudents = async () => {
      setLoadingStudents(true);
      setEvaluations({});
      try {
        const attendanceDate = dateInIstanbul(0);
        const path = `/admin/students-attendance/${encodeURIComponent(attendanceDate)}/school/${encodeURIComponent(schoolId)}?class_id=${encodeURIComponent(classId)}`;
        const response = await fetchJson(apiUrl(path));
        if (!response.ok) throw new Error('students');
        const data = await response.json();
        if (cancelled) return;
        const rows = (Array.isArray(data) ? data : [])
          .map((item) => ({
            ...item,
            id: String(item?.student_id ?? item?.id ?? ''),
            displayName: studentName(item),
            isAbsent: String(item?.status || '').toLowerCase() === 'absent',
          }))
          .filter((item) => item.id)
          .sort((a, b) => (
            a.displayName.localeCompare(b.displayName, 'tr-TR', { sensitivity: 'base' }) ||
            Number(a.id || 0) - Number(b.id || 0)
          ));
        setStudents(rows);
      } catch (_) {
        if (!cancelled) {
          setStudents([]);
          setFormError('Öğrenci listesi yüklenemedi.');
        }
      } finally {
        if (!cancelled) setLoadingStudents(false);
      }
    };
    loadStudents();
    return () => { cancelled = true; };
  }, [open, schoolId, classId, setEvaluations, setFormError]);

  const submit = (confirmed = false) => draft.submit({ confirmed,
    valid: !loadingLessons && !!classId && lessons.includes(lesson) && (isTeacher || !!teacherId),
    payload: { class_id: classId, lesson, assigned_date: assignedDate, teacher_id: teacherId || null,
      records: selectedStudents.map(student => ({ student_id: student.id, status: evaluations[student.id] })) },
  });

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Hızlı Ödev Değerlendirme</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Önceden sisteme kaydedilmemiş bir ödevi değerlendirin. Veli mesajları yalnızca işlemi tamamladığınızda gönderilir.
        </Typography>

        {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <Autocomplete
            size="small"
            fullWidth
            disabled={locked}
            options={classes || []}
            getOptionLabel={(option) => option?.name || ''}
            value={(classes || []).find((item) => String(item.id) === classId) || null}
            onChange={(_, value) => setClassId(String(value?.id || ''))}
            renderInput={(params) => <TextField {...params} label="Şube" required />}
          />
          {!isTeacher && (
            <FormControl size="small" fullWidth required disabled={locked}>
              <InputLabel id="quick-evaluation-teacher-label">Öğretmen</InputLabel>
              <Select
                labelId="quick-evaluation-teacher-label"
                label="Öğretmen"
                value={teacherId}
                onChange={(event) => {
                  const value = String(event.target.value || '');
                  setTeacherId(value);
                }}
              >
                {(teachers || []).map((teacher) => (
                  <MenuItem key={teacher.id} value={String(teacher.id)}>{teacher.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            select
            size="small"
            fullWidth
            required
            label="Ders"
            disabled={locked || loadingLessons || !lessons.length}
            value={lesson}
            onChange={(event) => setLesson(event.target.value)}
          >
            {lessons.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
          </TextField>
          <TextField
            size="small"
            fullWidth
            required
            disabled={locked}
            type="date"
            label="Ödevin Verildiği Tarih"
            value={assignedDate}
            onChange={(event) => setAssignedDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: dateInIstanbul(0) }}
          />
        </Stack>

        <Divider sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Öğrenci Değerlendirmeleri</Typography>
            <Typography variant="caption" color="text.secondary">
              Derste bulunan: {evaluatedCount}/{activeStudents.length} değerlendirildi
              {absentCount > 0 ? ` • Devamsız: ${evaluatedAbsentCount}/${absentCount} ayrıca değerlendirildi (toplu işlem dışında)` : ''}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" color="success" onClick={markAllDone} disabled={locked || loadingStudents || !activeStudents.length}>
            Kalanları Tamamladı Yap
          </Button>
        </Box>

        {loadingStudents ? (
          <List>{Array.from({ length: 5 }).map((_, index) => <ListItem key={index} divider><Skeleton width="100%" /></ListItem>)}</List>
        ) : students.length === 0 ? (
          <Alert severity="info">Bu şubede öğrenci bulunamadı.</Alert>
        ) : (
          <List disablePadding>
            {students.map((student) => (
              <ListItem key={student.id} divider disableGutters sx={{
                py: 1,
                px: student.isAbsent ? 1 : 0,
                borderLeft: student.isAbsent ? '4px solid' : undefined,
                borderColor: student.isAbsent ? 'warning.main' : undefined,
                borderRadius: student.isAbsent ? 1 : undefined,
                backgroundColor: student.isAbsent
                  ? (theme) => alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.12)
                  : undefined,
              }}>
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600 }}>{student.displayName}</Typography>
                    {student.isAbsent && <Chip size="small" color="warning" variant="outlined" label="Devamsız — isteğe bağlı değerlendirme" />}
                  </Box>
                  <ButtonGroup size="small" aria-label={`${student.displayName} ödev durumu`}>
                      {STATUS_OPTIONS.map((option) => (
                        <Button
                          key={option.value}
                          disabled={locked}
                          color={option.color}
                          variant={evaluations[student.id] === option.value ? 'contained' : 'outlined'}
                          onClick={() => setStatus(student.id, option.value)}
                          sx={{ textTransform: 'none', fontWeight: 700 }}
                        >
                          {option.label}
                        </Button>
                      ))}
                  </ButtonGroup>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>İptal</Button>
        <Button
          variant="contained"
          onClick={() => submit()}
          disabled={saving || loadingStudents || loadingLessons || !students.length || !lesson || !lessons.includes(lesson)}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {saving ? 'Kaydediliyor…' : 'Kaydet ve Velilere Gönder'}
        </Button>
      </DialogActions>
      <IncompleteQuickEvaluationDialog open={confirmIncomplete} count={remainingCount}
        onReturn={() => setConfirmIncomplete(false)} onSubmit={() => submit(true)} />
    </Dialog>
  );
}
